import React, { useEffect, useState, useRef, useCallback } from "react";
import { KatProgress } from "@amzn/katal-react";
import { useTranslation } from "react-i18next";
import { type UploadProgress, UploadStatus } from "src/api/interfaces/nest";
import client from "src/api/client";
import { fileToBase64 } from "../../../utils";
import "./index.css";

interface AnimatedItem {
  upload: UploadProgress;
  exiting: boolean;
}

interface NestUploadProgressFooterProps {
  files: File[];
  useCaseId: string;
  documentTypeId: string;
  onComplete: (results: UploadProgress[]) => void;
}

export function NestUploadProgressFooter({
  files,
  useCaseId,
  documentTypeId,
  onComplete,
}: NestUploadProgressFooterProps) {
  const { t } = useTranslation();
  const [exiting, setExiting] = useState(false);
  const [uploads, setUploads] = useState<UploadProgress[]>([]);
  const [items, setItems] = useState<AnimatedItem[]>([]);
  const prevKeys = useRef<Set<string>>(new Set());
  const hasStarted = useRef(false);

  const processSingleUpload = useCallback(
    async (upload: UploadProgress): Promise<UploadProgress> => {
      let interval: ReturnType<typeof setInterval> | undefined;
      try {
        const fileContent = await fileToBase64(upload.file);
        const startTime = Date.now();
        const fiveSeconds = 5000;

        interval = setInterval(() => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min((elapsed / fiveSeconds) * 90, 90);
          setUploads((prev) =>
            prev.map((u) =>
              u.fileName === upload.fileName ? { ...u, progress } : u
            )
          );
        }, 100);

        const testResult = await client.nestSubmit({
          useCaseId,
          fileName: upload.fileName,
          fileContent,
          documentTypeId,
        });
        clearInterval(interval);

        const done = {
          ...upload,
          progress: 100,
          status: UploadStatus.DONE,
          testResult,
        };
        setUploads((prev) =>
          prev.map((u) => (u.fileName === upload.fileName ? done : u))
        );
        return done;
      } catch (err: any) {
        if (interval) clearInterval(interval);
        const statusCode = err?.response?.status || 500;
        const error = err?.response?.data || {
          code: "500",
          message: { key: "error-unknown" },
        };
        const failed = {
          ...upload,
          progress: 100,
          status: UploadStatus.ERROR,
          error,
          statusCode,
        };
        setUploads((prev) =>
          prev.map((u) => (u.fileName === upload.fileName ? failed : u))
        );
        return failed;
      }
    },
    [useCaseId, documentTypeId]
  );

  // Start uploads when files arrive
  useEffect(() => {
    if (!files.length || hasStarted.current) return;
    hasStarted.current = true;

    const initial: UploadProgress[] = files.map((f) => ({
      fileName: f.name,
      progress: 0,
      status: UploadStatus.UPLOADING,
      file: f,
    }));
    setUploads(initial);

    Promise.all(initial.map((u) => processSingleUpload(u))).then(onComplete);
  }, [files, processSingleUpload, onComplete]);

  // Animation logic
  const completed = uploads.filter(
    (u) => u.status === UploadStatus.DONE || u.status === UploadStatus.ERROR
  ).length;
  const allDone = uploads.length > 0 && completed === uploads.length;

  const displayedUploads = [
    ...uploads.filter((u) => u.status === UploadStatus.UPLOADING).slice(0, 1),
    ...uploads
      .filter(
        (u) => u.status === UploadStatus.DONE || u.status === UploadStatus.ERROR
      )
      .slice(-1),
  ].slice(0, 2);

  useEffect(() => {
    const currentKeys = new Set(displayedUploads.map((u) => u.fileName));
    const exitingKeys = [...prevKeys.current].filter(
      (k) => !currentKeys.has(k)
    );

    setItems((prev) => {
      const updated = prev
        .map((item) =>
          exitingKeys.includes(item.upload.fileName)
            ? { ...item, exiting: true }
            : item
        )
        .filter(
          (item) => currentKeys.has(item.upload.fileName) || item.exiting
        );

      const newItems = displayedUploads
        .filter((u) => !prev.some((p) => p.upload.fileName === u.fileName))
        .map((upload) => ({ upload, exiting: false }));

      return [...updated, ...newItems];
    });

    if (exitingKeys.length) {
      setTimeout(() => {
        setItems((prev) => prev.filter((item) => !item.exiting));
      }, 300);
    }

    prevKeys.current = currentKeys;
  }, [displayedUploads.map((u) => u.fileName).join()]);

  useEffect(() => {
    if (allDone) {
      const timer = setTimeout(() => {
        setExiting(true);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [allDone]);

  return (
    <div
      className={`nest-upload-footer${exiting ? " nest-upload-footer--exiting" : ""}`}
    >
      <div className="nest-upload-footer__header">
        <span className="nest-upload-footer__title">
          {t("nest_upload_progress", { completed, total: uploads.length })}
        </span>
      </div>
      <div className="nest-upload-footer__list">
        {items.map(({ upload, exiting: itemExiting }) => {
          const currentUpload =
            uploads.find((u) => u.fileName === upload.fileName) || upload;
          return (
            <div
              key={upload.fileName}
              className={`nest-upload-footer__item${itemExiting ? " nest-upload-footer__item--exiting" : ""}`}
            >
              <div className="nest-upload-footer__filename">
                <span>{currentUpload.fileName}</span>
              </div>
              <KatProgress
                size="small"
                value={currentUpload.progress}
                state={
                  currentUpload.status === UploadStatus.DONE
                    ? "success"
                    : currentUpload.status === UploadStatus.ERROR
                      ? "error"
                      : "working"
                }
                className={
                  currentUpload.status === UploadStatus.UPLOADING
                    ? "nest-upload-footer__progress--uploading"
                    : ""
                }
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
